import type { FieldArrayWithId, UseFieldArrayRemove, UseFieldArrayUpdate } from "react-hook-form";

import { Form } from "@/components/Form/Form";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Stack } from "@/design-system/Stack";
import { createRequiredStringRule } from "@/helpers/rules";
import type { AutomatedSurveyQuestionCategory } from "@/modules/system-settings/automated-surveys/constants";
import { useTranslation } from "@/translations";

import type { FormValues } from "../AutomatedSurveyForm";
import { MAX_LENGTH_QUESTION } from "../constants";

interface Props {
  disabled: boolean;
  categories: AutomatedSurveyQuestionCategory[];
  question: FieldArrayWithId<FormValues, "questions", "id">;
  idx: number;
  showDelete?: boolean;
  updateField: UseFieldArrayUpdate<FormValues, "questions">;
  removeField: UseFieldArrayRemove;
}

export function FormQuestion({
  disabled,
  categories,
  question,
  idx,
  showDelete,
  updateField,
  removeField,
}: Props): React.ReactNode {
  const { t } = useTranslation();

  function onBlurQuestion(type: "ratingQuestion" | "openQuestion" | "explanation", value: string) {
    updateField(idx, {
      ...question,
      [type]: value || "",
    });
  }

  return (
    <Stack gap="lg">
      <Form.SplitContent>
        <FormField label={t("page.automated-surveys.new.form.category.label")} required>
          <FormSelect<FormValues, AutomatedSurveyQuestionCategory>
            name={`questions.${idx}.category`}
            items={categories}
            defaultValue={question.category}
            keySelector={(x) => x.id}
            renderOption={(x) => {
              switch (x.id) {
                case "building":
                  return t("page.automated-surveys.question-type.building");
                case "community":
                  return t("page.automated-surveys.question-type.community");
                case "service":
                  return t("page.automated-surveys.question-type.service");
                case "feelAtHome":
                  return t("page.automated-surveys.question-type.feeling-at-home");
                case "other":
                  return t("page.automated-surveys.types.other");
              }
            }}
            // This update forces a re-render, so that we can show the delete buttons on the right questions
            onChange={(cat) => updateField(idx, { ...question, category: cat! })}
            rules={{
              required: t("components.form.error.required", {
                inputName: t("page.automated-surveys.new.form.category.label"),
              }),
            }}
            disabled={disabled}
          />
        </FormField>
        <FormField label={t("page.automated-surveys.new.form.rating-question.label")} required>
          <FormInput<FormValues>
            name={`questions.${idx}.ratingQuestion`}
            onBlur={(e) => onBlurQuestion("ratingQuestion", e.target.value)}
            placeholder={t("page.automated-surveys.new.form.rating-question.placeholder")}
            rules={{
              maxLength: {
                message: t("components.form.error.max-length", { length: MAX_LENGTH_QUESTION }),
                value: MAX_LENGTH_QUESTION,
              },
              validate: {
                required: createRequiredStringRule(t, "page.automated-surveys.new.form.rating-question.label"),
              },
            }}
          />
        </FormField>
      </Form.SplitContent>
      <FormField label={t("page.automated-surveys.new.form.open-question.label")}>
        <FormInput<FormValues>
          name={`questions.${idx}.openQuestion`}
          onBlur={(e) => onBlurQuestion("openQuestion", e.target.value)}
          placeholder={t("page.automated-surveys.new.form.open-question.placeholder")}
          rules={{
            maxLength: {
              message: t("components.form.error.max-length", { length: MAX_LENGTH_QUESTION }),
              value: MAX_LENGTH_QUESTION,
            },
          }}
        />
      </FormField>
      <FormField label={t("page.automated-surveys.new.form.question-explanation.label")}>
        <FormInput<FormValues>
          name={`questions.${idx}.explanation`}
          onBlur={(e) => onBlurQuestion("explanation", e.target.value)}
          placeholder={t("page.automated-surveys.new.form.question-explanation.placeholder")}
          rules={{
            maxLength: {
              message: t("components.form.error.max-length", { length: MAX_LENGTH_QUESTION }),
              value: MAX_LENGTH_QUESTION,
            },
          }}
        />
      </FormField>
      {showDelete && (
        <footer className="self-end">
          <Button variant="filled" tone="error" onClick={() => removeField(idx)}>
            {t("common.action.delete")}
          </Button>
        </footer>
      )}
    </Stack>
  );
}
